import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { TEST_IDS } from "../constants/testIds";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Rocket, Users, ListChecks } from "lucide-react";

const STEPS = 3;

export default function OnboardingWizard({ open, onClose }) {
  const { t } = useTranslation();
  const { user, tenant } = useAuthStore();
  const [step, setStep] = useState(0);
  const [projectName, setProjectName] = useState("Q2 Roadmap");
  const [inviteEmails, setInviteEmails] = useState("");
  const [firstTaskTitle, setFirstTaskTitle] = useState("Set team OKRs");
  const [loading, setLoading] = useState(false);

  const finish = async () => {
    setLoading(true);
    try {
      // Create project if provided
      let projectId = null;
      let listId = null;
      if (projectName.trim()) {
        const { data: proj } = await api.post("/projects", {
          name: projectName.trim(),
        });
        projectId = proj.id;
        const { data: list } = await api.post("/lists", {
          project_id: projectId,
          name: "Backlog",
          owner_id: user.id,
        });
        listId = list.id;
      }
      // Invite teammates
      const emails = inviteEmails
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes("@"));
      for (const email of emails) {
        try {
          await api.post("/users/invite", {
            email,
            name: email.split("@")[0],
            role: "user",
          });
        } catch {
          /* ignore per-email errors */
        }
      }
      // First task
      if (listId && firstTaskTitle.trim()) {
        await api.post("/tasks", {
          project_id: projectId,
          list_id: listId,
          title: firstTaskTitle.trim(),
          priority: "high",
          assignee_id: user.id,
        });
      }
      await api.post("/users/me/complete-onboarding");
      toast.success(t("onboarding.done"));
      onClose(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const skip = async () => {
    try {
      await api.post("/users/me/complete-onboarding");
    } catch {
      /* ignore */
    }
    onClose(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && skip()}>
      <DialogContent
        className="max-w-lg"
        data-testid={TEST_IDS.onboardingDialog}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {t("onboarding.title", { name: user?.name?.split(" ")[0] || "there" })}
          </DialogTitle>
          <DialogDescription>{t("onboarding.subtitle")}</DialogDescription>
        </DialogHeader>

        {/* Progress dots */}
        <div className="mb-2 flex items-center gap-1.5">
          {Array.from({ length: STEPS }).map((_, i) => (
            <div
              key={i}
              className={[
                "h-1.5 flex-1 rounded-full transition-colors",
                i <= step ? "bg-zinc-900" : "bg-zinc-200",
              ].join(" ")}
            />
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4" data-testid="onboarding-step-1">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-50">
              <Rocket className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">
                {t("onboarding.step1_title")}
              </div>
              <p className="mt-1 text-sm text-zinc-500">
                {t("onboarding.step1_desc")}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("projects.name")}
              </Label>
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                data-testid={TEST_IDS.onboardingProjectName}
                className="mt-1"
              />
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4" data-testid="onboarding-step-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-green-50">
              <Users className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">
                {t("onboarding.step2_title")}
              </div>
              <p className="mt-1 text-sm text-zinc-500">
                {t("onboarding.step2_desc")}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("onboarding.emails_label")}
              </Label>
              <Input
                placeholder="alice@team.com, bob@team.com"
                value={inviteEmails}
                onChange={(e) => setInviteEmails(e.target.value)}
                data-testid={TEST_IDS.onboardingInvites}
                className="mt-1"
              />
              <p className="mt-1 text-xs text-zinc-500">
                {t("onboarding.emails_hint")}
              </p>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4" data-testid="onboarding-step-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-purple-50">
              <ListChecks className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">
                {t("onboarding.step3_title")}
              </div>
              <p className="mt-1 text-sm text-zinc-500">
                {t("onboarding.step3_desc")}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("tasks.title")}
              </Label>
              <Input
                value={firstTaskTitle}
                onChange={(e) => setFirstTaskTitle(e.target.value)}
                data-testid={TEST_IDS.onboardingFirstTask}
                className="mt-1"
              />
            </div>
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600">
              <CheckCircle2 className="mr-1 inline h-3 w-3 text-zinc-900" />
              {t("onboarding.summary", {
                project: projectName || "—",
                invites:
                  inviteEmails
                    .split(/[\s,;]+/)
                    .filter((e) => e.includes("@")).length,
              })}
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between border-t border-zinc-100 pt-4 mt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={skip}
            className="text-zinc-500"
            data-testid={TEST_IDS.onboardingSkip}
          >
            {t("onboarding.skip")}
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep((s) => s - 1)}
              >
                {t("onboarding.back")}
              </Button>
            )}
            {step < STEPS - 1 ? (
              <Button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                data-testid={TEST_IDS.onboardingNext}
                className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
              >
                {t("onboarding.next")}
                <ArrowRight className="h-3 w-3" />
              </Button>
            ) : (
              <Button
                type="button"
                disabled={loading}
                onClick={finish}
                data-testid={TEST_IDS.onboardingFinish}
                className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
              >
                {loading ? "…" : t("onboarding.finish")}
                <CheckCircle2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
